/**
 * useSubstrateLogs Hook
 *
 * Provides CRUD operations and profitability queries for the third-party
 * substrate tracking system (shiitake logs).
 *
 * Tables: substrate_log_purchases, substrate_inductions,
 *         substrate_induction_harvests, substrate_overhead_costs
 *
 * @example
 * const {
 *   purchases, inductions, overheads,
 *   profitability,
 *   addPurchase, addInduction, addHarvest, addOverhead,
 *   updateInductionStatus, deleteHarvest,
 *   isLoading, error, refresh
 * } = useSubstrateLogs({ periodStart: '2026-03-01', periodEnd: '2026-03-31' })
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'
import { toLocalDateString } from '../utils/dateUtils'

// =============================================================================
// Constants
// =============================================================================

const OVERHEAD_CATEGORIES = ['container_rental', 'electricity', 'transport', 'other']
const RUNNING_COST_CATEGORIES = ['energy', 'maintenance', 'consumables', 'labour', 'other']
const INDUCTION_STATUSES = ['inducted', 'fruiting', 'harvesting', 'resting', 'spent']
const QUALITY_GRADES = ['A+', 'A', 'B', 'reject']
const ARRANGEMENT_TYPES = ['cost_share', 'sale']

// =============================================================================
// Main Hook
// =============================================================================

export function useSubstrateLogs({ periodStart, periodEnd } = {}) {
  const [purchases, setPurchases] = useState([])
  const [inductions, setInductions] = useState([])
  const [overheads, setOverheads] = useState([])
  const [runningCosts, setRunningCosts] = useState([])
  const [partners, setPartners] = useState([])
  const [shipments, setShipments] = useState([])
  const [costShares, setCostShares] = useState([])
  const [profitability, setProfitability] = useState(null)
  const [partnerXeroInvoices, setPartnerXeroInvoices] = useState({})
  const [partnerXeroLoading, setPartnerXeroLoading] = useState(false)
  const [partnerXeroError, setPartnerXeroError] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // =========================================================================
  // Fetch Functions
  // =========================================================================

  const fetchPurchases = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('substrate_log_purchases')
      .select('*, strains(name, code)')
      .order('purchase_date', { ascending: false })

    if (err) throw new Error(err.message)
    return data || []
  }, [])

  const fetchInductions = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('substrate_inductions')
      .select(`
        *,
        strains(name, code),
        substrate_log_purchases(supplier_name, cost_per_log, unit_weight_kg, target_yield_percent),
        substrate_induction_harvests(
          id, harvest_date, flush_number, weight_kg, quality_grade, harvested_by, notes, created_at
        )
      `)
      .order('induction_date', { ascending: false })

    if (err) throw new Error(err.message)
    return data || []
  }, [])

  const fetchOverheads = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('substrate_overhead_costs')
      .select('*')
      .order('period_start', { ascending: false })

    if (err) throw new Error(err.message)
    return data || []
  }, [])

  const fetchRunningCosts = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('substrate_running_costs')
      .select('*, substrate_log_purchases(purchase_code, supplier_name)')
      .order('cost_date', { ascending: false })

    if (err) throw new Error(err.message)
    return data || []
  }, [])

  const fetchPartners = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('substrate_partners')
      .select('*')
      .order('name')

    if (err) throw new Error(err.message)
    return data || []
  }, [])

  const fetchShipments = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('substrate_log_shipments')
      .select('*, substrate_partners(name), substrate_log_purchases(supplier_name, purchase_date, purchase_code)')
      .order('shipment_date', { ascending: false })

    if (err) throw new Error(err.message)
    return data || []
  }, [])

  const fetchCostShares = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('partner_cost_shares')
      .select('*, substrate_partners(name)')
      .order('effective_from', { ascending: false })

    if (err) throw new Error(err.message)
    return data || []
  }, [])

  const fetchProfitability = useCallback(async () => {
    if (!periodStart || !periodEnd) return null

    const { data, error: err } = await supabase
      .rpc('calculate_substrate_profitability', {
        p_start: periodStart,
        p_end: periodEnd
      })

    if (err) throw new Error(err.message)
    return data
  }, [periodStart, periodEnd])

  // =========================================================================
  // Load All Data
  // =========================================================================

  const refresh = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const [purchasesData, inductionsData, overheadsData, runningCostsData, partnersData, shipmentsData, costSharesData, profitData] = await Promise.all([
        fetchPurchases(),
        fetchInductions(),
        fetchOverheads(),
        fetchRunningCosts(),
        fetchPartners(),
        fetchShipments(),
        fetchCostShares(),
        fetchProfitability()
      ])

      setPurchases(purchasesData)
      setInductions(inductionsData)
      setOverheads(overheadsData)
      setRunningCosts(runningCostsData)
      setPartners(partnersData)
      setShipments(shipmentsData)
      setCostShares(costSharesData)
      setProfitability(profitData)
    } catch (err) {
      console.error('[useSubstrateLogs] Error:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [fetchPurchases, fetchInductions, fetchOverheads, fetchRunningCosts, fetchPartners, fetchShipments, fetchCostShares, fetchProfitability])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Auto-fetch Xero invoices for all linked partners after initial load
  useEffect(() => {
    if (isLoading || partners.length === 0) return
    const linkedPartners = partners.filter(p => p.xero_contact_id && p.is_active)
    if (linkedPartners.length === 0) return

    // Only fetch if we haven't already
    const unfetched = linkedPartners.filter(p => !partnerXeroInvoices[p.id])
    if (unfetched.length === 0) return

    unfetched.forEach(partner => {
      fetchPartnerXeroInvoices(partner)
    })
  }, [isLoading, partners]) // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // Derived Data
  // =========================================================================

  const totalLogsInStorage = useMemo(() => {
    return purchases.reduce((sum, p) => sum + (p.logs_remaining || 0), 0)
  }, [purchases])

  const activeInductions = useMemo(() => {
    return inductions.filter(i => i.status !== 'spent')
  }, [inductions])

  const totalLogsShipped = useMemo(() => {
    return shipments.reduce((sum, s) => sum + (s.log_count || 0), 0)
  }, [shipments])

  // =========================================================================
  // CRUD: Purchases
  // =========================================================================

  const addPurchase = useCallback(async (purchaseData) => {
    // Generate purchase code via RPC
    const { data: code, error: codeErr } = await supabase
      .rpc('generate_purchase_code', { p_date: purchaseData.purchase_date })
    if (codeErr) throw new Error(codeErr.message)

    const { data, error: err } = await supabase
      .from('substrate_log_purchases')
      .insert({
        ...purchaseData,
        purchase_code: code,
        logs_remaining: purchaseData.quantity
      })
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const updatePurchase = useCallback(async (id, updates) => {
    const { data, error: err } = await supabase
      .from('substrate_log_purchases')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const deletePurchase = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('substrate_log_purchases')
      .delete()
      .eq('id', id)

    if (err) throw new Error(err.message)
    await refresh()
  }, [refresh])

  // =========================================================================
  // CRUD: Inductions
  // =========================================================================

  const generateInductionCode = useCallback(async (date) => {
    const { data, error: err } = await supabase
      .rpc('generate_induction_code', { p_date: date })

    if (err) throw new Error(err.message)
    return data
  }, [])

  const addInduction = useCallback(async (inductionData) => {
    // Generate induction code server-side
    const code = await generateInductionCode(inductionData.induction_date)

    const { data, error: err } = await supabase
      .from('substrate_inductions')
      .insert({
        ...inductionData,
        induction_code: code
      })
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [generateInductionCode, refresh])

  const updateInduction = useCallback(async (id, updates) => {
    // If log_count changed, adjust logs_remaining on the source purchase
    if (updates.log_count !== undefined) {
      const current = inductions.find(i => i.id === id)
      if (current && updates.log_count !== current.log_count) {
        const diff = updates.log_count - current.log_count
        const purchase = purchases.find(p => p.id === current.purchase_id)
        if (purchase) {
          const newRemaining = purchase.logs_remaining - diff
          if (newRemaining < 0) {
            throw new Error(`Only ${purchase.logs_remaining + current.log_count} logs available from this purchase`)
          }
          await supabase
            .from('substrate_log_purchases')
            .update({ logs_remaining: newRemaining, updated_at: new Date().toISOString() })
            .eq('id', current.purchase_id)
        }
      }
    }

    const { data, error: err } = await supabase
      .from('substrate_inductions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [inductions, purchases, refresh])

  const updateInductionStatus = useCallback(async (id, status) => {
    const updates = {
      status,
      updated_at: new Date().toISOString()
    }
    if (status === 'spent') {
      updates.spent_date = toLocalDateString(new Date())
    }

    const { data, error: err } = await supabase
      .from('substrate_inductions')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const deleteInduction = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('substrate_inductions')
      .delete()
      .eq('id', id)

    if (err) throw new Error(err.message)
    await refresh()
  }, [refresh])

  // =========================================================================
  // CRUD: Harvests
  // =========================================================================

  const addHarvest = useCallback(async (harvestData) => {
    const { data, error: err } = await supabase
      .from('substrate_induction_harvests')
      .insert(harvestData)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const updateHarvest = useCallback(async (id, updates) => {
    const { data, error: err } = await supabase
      .from('substrate_induction_harvests')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const deleteHarvest = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('substrate_induction_harvests')
      .delete()
      .eq('id', id)

    if (err) throw new Error(err.message)
    await refresh()
  }, [refresh])

  // =========================================================================
  // CRUD: Overheads
  // =========================================================================

  const addOverhead = useCallback(async (overheadData) => {
    const { data, error: err } = await supabase
      .from('substrate_overhead_costs')
      .insert(overheadData)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const updateOverhead = useCallback(async (id, updates) => {
    const { data, error: err } = await supabase
      .from('substrate_overhead_costs')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const deleteOverhead = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('substrate_overhead_costs')
      .delete()
      .eq('id', id)

    if (err) throw new Error(err.message)
    await refresh()
  }, [refresh])

  const copyOverheadsFromMonth = useCallback(async (sourceStart, sourceEnd, targetStart, targetEnd) => {
    // Fetch source month overheads
    const { data: sourceData, error: fetchErr } = await supabase
      .from('substrate_overhead_costs')
      .select('category, description, amount')
      .gte('period_start', sourceStart)
      .lte('period_end', sourceEnd)

    if (fetchErr) throw new Error(fetchErr.message)
    if (!sourceData?.length) throw new Error('No overhead costs found in source month')

    // Insert copies for target month
    const copies = sourceData.map(item => ({
      ...item,
      period_start: targetStart,
      period_end: targetEnd
    }))

    const { error: insertErr } = await supabase
      .from('substrate_overhead_costs')
      .insert(copies)

    if (insertErr) throw new Error(insertErr.message)
    await refresh()
  }, [refresh])

  // =========================================================================
  // CRUD: Running Costs
  // =========================================================================

  const addRunningCost = useCallback(async (costData) => {
    const { data, error: err } = await supabase
      .from('substrate_running_costs')
      .insert(costData)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const updateRunningCost = useCallback(async (id, updates) => {
    const { data, error: err } = await supabase
      .from('substrate_running_costs')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const deleteRunningCost = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('substrate_running_costs')
      .delete()
      .eq('id', id)

    if (err) throw new Error(err.message)
    await refresh()
  }, [refresh])

  // =========================================================================
  // CRUD: Partners
  // =========================================================================

  const addPartner = useCallback(async (partnerData) => {
    const { data, error: err } = await supabase
      .from('substrate_partners')
      .insert(partnerData)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const updatePartner = useCallback(async (id, updates) => {
    const { data, error: err } = await supabase
      .from('substrate_partners')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const deactivatePartner = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('substrate_partners')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', id)

    if (err) throw new Error(err.message)
    await refresh()
  }, [refresh])

  const reactivatePartner = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('substrate_partners')
      .update({ is_active: true, updated_at: new Date().toISOString() })
      .eq('id', id)

    if (err) throw new Error(err.message)
    await refresh()
  }, [refresh])

  // =========================================================================
  // CRUD: Shipments
  // =========================================================================

  const addShipment = useCallback(async (shipmentData) => {
    const { data, error: err } = await supabase
      .from('substrate_log_shipments')
      .insert(shipmentData)
      .select()
      .single()

    if (err) throw new Error(err.message)
    // Refresh in background — don't let refresh errors mask a successful insert
    refresh().catch(e => console.error('[useSubstrateLogs] Refresh after addShipment:', e))
    return data
  }, [refresh])

  const updateShipment = useCallback(async (id, updates) => {
    const { data, error: err } = await supabase
      .from('substrate_log_shipments')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const deleteShipment = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('substrate_log_shipments')
      .delete()
      .eq('id', id)

    if (err) throw new Error(err.message)
    await refresh()
  }, [refresh])

  // =========================================================================
  // CRUD: Cost Shares
  // =========================================================================

  const addCostShare = useCallback(async (costShareData) => {
    const { data, error: err } = await supabase
      .from('partner_cost_shares')
      .insert(costShareData)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const updateCostShare = useCallback(async (id, updates) => {
    const { data, error: err } = await supabase
      .from('partner_cost_shares')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()

    if (err) throw new Error(err.message)
    await refresh()
    return data
  }, [refresh])

  const deleteCostShare = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('partner_cost_shares')
      .delete()
      .eq('id', id)

    if (err) throw new Error(err.message)
    await refresh()
  }, [refresh])

  // =========================================================================
  // Xero: Partner Invoice Fetching (on-demand)
  // =========================================================================

  const fetchPartnerXeroInvoices = useCallback(async (partner) => {
    if (!partner?.xero_contact_id) return

    setPartnerXeroLoading(true)
    setPartnerXeroError(null)

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'xero-invoices',
        { body: { action: 'list-by-contact', contactId: partner.xero_contact_id } }
      )

      if (invokeError) throw new Error(invokeError.message || 'Failed to fetch invoices')
      if (!data) throw new Error('No response from Xero')
      if (!data.success) throw new Error(data.message || 'Failed to list invoices')

      setPartnerXeroInvoices(prev => ({
        ...prev,
        [partner.id]: data.invoices
      }))
    } catch (err) {
      console.error('[useSubstrateLogs] Xero invoice fetch error:', err)
      setPartnerXeroError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setPartnerXeroLoading(false)
    }
  }, [])

  // =========================================================================
  // Return
  // =========================================================================

  return {
    // Data
    purchases,
    inductions,
    activeInductions,
    overheads,
    runningCosts,
    partners,
    shipments,
    costShares,
    profitability,
    totalLogsInStorage,
    totalLogsShipped,

    // CRUD: Purchases
    addPurchase,
    updatePurchase,
    deletePurchase,

    // CRUD: Inductions
    addInduction,
    updateInduction,
    updateInductionStatus,
    deleteInduction,

    // CRUD: Harvests
    addHarvest,
    updateHarvest,
    deleteHarvest,

    // CRUD: Overheads
    addOverhead,
    updateOverhead,
    deleteOverhead,
    copyOverheadsFromMonth,

    // CRUD: Running Costs
    addRunningCost,
    updateRunningCost,
    deleteRunningCost,

    // CRUD: Partners
    addPartner,
    updatePartner,
    deactivatePartner,
    reactivatePartner,

    // CRUD: Shipments
    addShipment,
    updateShipment,
    deleteShipment,

    // CRUD: Cost Shares
    addCostShare,
    updateCostShare,
    deleteCostShare,

    // Xero: Partner Invoices
    partnerXeroInvoices,
    partnerXeroLoading,
    partnerXeroError,
    fetchPartnerXeroInvoices,

    // Status
    isLoading,
    error,
    refresh,

    // Constants (for forms)
    OVERHEAD_CATEGORIES,
    RUNNING_COST_CATEGORIES,
    INDUCTION_STATUSES,
    QUALITY_GRADES,
    ARRANGEMENT_TYPES
  }
}

export { OVERHEAD_CATEGORIES, RUNNING_COST_CATEGORIES, INDUCTION_STATUSES, QUALITY_GRADES, ARRANGEMENT_TYPES }
